import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface ClassifyStageOption {
  v: string;
  l: string;
}

export interface ClassifyRowItem {
  accountId: string;
  cmName: string;
  alias: string;
  platform: string;
  capital: string;
  balance: string;
  perf: string;
  perfColor: string;
  dd: string;
  ddColor: string;
  status: string;
  statusColor: string;
  value: string;
  border: string;
  bg: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: ClassifyStageOption[];
}

export interface StageClassifyModalProps {
  isOpen: boolean;
  closeModal: () => void;
  title: string;
  classifyRows: ClassifyRowItem[];
  draftCount: number;
  saveBg: string;
  saveClassification: () => void;
}

export const StageClassifyModal: React.FC<StageClassifyModalProps> = ({
  isOpen,
  closeModal,
  title,
  classifyRows,
  draftCount,
  saveBg,
  saveClassification
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={closeModal}
      style={s(
        `position:fixed;inset:0;background:rgba(1,37,60,0.45);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:6vh 16px;overflow-y:auto`
      )}
    >
      <div
        onClick={stop}
        style={s(
          `width:100%;max-width:1060px;background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(1,37,60,0.3);overflow:hidden`
        )}
      >
        <div
          style={s(
            `display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:20px 24px;border-bottom:1px solid #EEF0F3`
          )}
        >
          <div style={s(`display:flex;flex-direction:column;gap:4px`)}>
            <span style={s(`font-size:18px;font-weight:700;color:#0F1B2D`)}>{title}</span>
            <span style={s(`font-size:13px;color:#6B7280;text-wrap:pretty;max-width:720px`)}>
              {t('managerNetwork.classifyModal.desc', 'Trading data comes from bot/API, but it cannot tell whether an account is Demo, Challenge, Verified or Funded. Select a stage only when it is confirmed. Accounts left blank stay Unclassified.')}
            </span>
          </div>
          <button
            onClick={closeModal}
            style={s(
              `width:34px;height:34px;border-radius:8px;border:1px solid #E3E6EA;background:#fff;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex:none`
            )}
          >
            <i className="icon-x"></i>
          </button>
        </div>
        <div style={s(`overflow-x:auto;max-height:58vh;overflow-y:auto`)}>
          <div style={s(`min-width:940px`)}>
            <div
              style={s(
                `display:grid;grid-template-columns:minmax(180px,1.6fr) 76px 100px 116px 100px 80px 100px 160px;gap:8px;padding:10px 24px;background:#FAFAFB;font-size:11.5px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:0.03em;position:sticky;top:0;z-index:1`
              )}
            >
              <span>{t('managerNetwork.classifyModal.cols.accountId', 'Account ID')}</span>
              <span>{t('managerNetwork.classifyModal.cols.platform', 'Platform')}</span>
              <span>{t('managerNetwork.classifyModal.cols.initialCap', 'Initial cap.')}</span>
              <span>{t('managerNetwork.classifyModal.cols.balance', 'Balance')}</span>
              <span>{t('managerNetwork.classifyModal.cols.perf', 'Perf.')}</span>
              <span>{t('managerNetwork.classifyModal.cols.dd', 'DD')}</span>
              <span>{t('managerNetwork.classifyModal.cols.status', 'Status')}</span>
              <span>{t('managerNetwork.classifyModal.cols.stage', 'Stage')}</span>
            </div>
            {classifyRows?.map((c, c_idx) => (
              <React.Fragment key={c_idx}>
                <div
                  style={s(
                    `display:grid;grid-template-columns:minmax(180px,1.6fr) 76px 100px 116px 100px 80px 100px 160px;gap:8px;align-items:center;padding:10px 24px;border-bottom:1px solid #F1F2F5;font-size:13px;background:${c.bg}`
                  )}
                >
                  <div style={s(`display:flex;flex-direction:column;min-width:0`)}>
                    <span style={s(`font-weight:600;color:#0F1B2D`)}>{c.accountId}</span>
                    <span
                      style={s(
                        `font-size:11.5px;color:#6B7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
                      )}
                    >
                      {c.cmName} · {c.alias}
                    </span>
                  </div>
                  <span>{c.platform}</span>
                  <span>{c.capital}</span>
                  <span style={s(`font-weight:500`)}>{c.balance}</span>
                  <span style={s(`color:${c.perfColor};font-weight:500`)}>{c.perf}</span>
                  <span style={s(`color:${c.ddColor}`)}>{c.dd}</span>
                  <span style={s(`display:flex;align-items:center;gap:6px`)}>
                    <span style={s(`width:8px;height:8px;border-radius:50%;background:${c.statusColor}`)}></span>
                    {c.status}
                  </span>
                  <div style={s(`position:relative`)}>
                    <select
                      value={c.value}
                      onChange={c.onChange}
                      style={s(
                        `width:100%;height:34px;border:1px solid ${c.border};border-radius:8px;padding:0 28px 0 10px;font-size:13px;color:#1B2430;background:#fff;cursor:pointer;outline:0`
                      )}
                    >
                      <option value="">{t('managerNetwork.classifyModal.selectStage', 'Select stage…')}</option>
                      <option value="Demo">{t('managerNetwork.stages.Demo', 'Demo')}</option>
                      <option value="Challenge">{t('managerNetwork.stages.Challenge', 'Challenge')}</option>
                      <option value="Verified">{t('managerNetwork.stages.Verified', 'Verified')}</option>
                      <option value="Funded">{t('managerNetwork.stages.Funded', 'Funded')}</option>
                    </select>
                    <i className="icon-chevron-down" style={s(`position:absolute;right:8px;top:9px;color:#6B7280;pointer-events:none`)}></i>
                  </div>
                </div>
              </React.Fragment>
            ))}
            {classifyRows.length === 0 && (
              <div style={s(`padding:40px 24px;text-align:center;color:#6B7280;font-size:13px`)}>
                {t('managerNetwork.classifyModal.noAccounts', 'Không có tài khoản nào cần phân loại')}
              </div>
            )}
          </div>
        </div>
        <div
          style={s(
            `display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px;padding:16px 24px;border-top:1px solid #EEF0F3`
          )}
        >
          <span style={s(`font-size:13px;color:#6B7280`)}>
            {t('managerNetwork.classifyModal.selectedSummary', '{{count}} selected · saved with source “Admin” and added to stage history', {
              count: draftCount
            })}
          </span>
          <div style={s(`display:flex;gap:10px`)}>
            <button
              onClick={closeModal}
              style={s(
                `height:40px;padding:0 18px;border:1px solid #E3E6EA;border-radius:8px;background:#fff;color:#1B2430;font-size:14px;font-weight:500;cursor:pointer`
              )}
            >
              {t('managerNetwork.classifyModal.cancel', 'Cancel')}
            </button>
            <button
              onClick={saveClassification}
              style={s(
                `height:40px;padding:0 18px;border:0;border-radius:8px;background:${saveBg};color:#0F1B2D;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px`
              )}
            >
              <i className="icon-check"></i>
              {t('managerNetwork.classifyModal.save', 'Save Classification')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
